import React, { useState, useEffect, useRef, createElement, useMemo, useCallback } from "react";
import { getSurveyAnalytics, getSurveyWithResponses } from "../../serverComm";
import { OverviewSkeleton } from "./OverviewSkeleton";
import inputTypes from "../../assets/inputTypes";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { RefreshCw, Info } from "lucide-react";

const QUESTION_TYPE_ICONS = Object.keys(inputTypes).reduce((acc, key) => {
  acc[key] = {
    icon: inputTypes[key].icon,
    bgColor: inputTypes[key].bgColor,
    iconColor: inputTypes[key].iconColor,
  };
  return acc;
}, {});

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="bg-white shadow-lg rounded-lg px-4 py-3 border border-gray-100 text-xs">
      <div className="font-medium text-gray-700 mb-1">{label}</div>
      {payload.map((entry, i) => (
        <div key={i} className="flex justify-between gap-4 items-center">
          <span className="text-gray-500">{entry.name}:</span>
          <span className="font-semibold text-gray-900">{entry.value?.toLocaleString()}</span>
        </div>
      ))}
      {point.averageTime != null && (
        <div className="flex justify-between gap-4 items-center mt-1 pt-1 border-t border-gray-100">
          <span className="text-gray-500">Avg. time:</span>
          <span className="font-semibold text-gray-900">{formatTimeStatic(point.averageTime)}</span>
        </div>
      )}
    </div>
  );
};

const formatTimeStatic = (seconds) => {
  if (!seconds) return "0 min";
  return `${(seconds / 60).toFixed(1)} min`;
};

const SurveyAnalyticsOverview = ({ surveyId, survey }) => {
  const [surveyAnalyticsData, setSurveyAnalyticsData] = useState(null);
  const [allResponses, setAllResponses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const fetchIdRef = useRef(0);

  const fetchAllData = useCallback(async () => {
    const currentFetchId = ++fetchIdRef.current;

    try {
      setLoading(true);
      setError(null);

      const [analyticsData, allResponsesData] = await Promise.all([
        getSurveyAnalytics(surveyId),
        getSurveyWithResponses(surveyId, {}),
      ]);

      if (currentFetchId !== fetchIdRef.current) return;

      setSurveyAnalyticsData(analyticsData);
      setAllResponses(allResponsesData.responses || []);
      setLastUpdated(new Date());
    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) return;
      setError(err?.message || "Failed to fetch survey analytics data");
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [surveyId]);

  useEffect(() => {
    fetchAllData();
    return () => { fetchIdRef.current++; };
  }, [fetchAllData]);

  const formatTime = (seconds) => formatTimeStatic(seconds);

  // Unified completion-rate definition: completed / starts when start tracking
  // exists (respondents who moved past the welcome screen), falling back to
  // completed / views for surveys created before start tracking.
  const getCompletionRate = () => {
    const responseCount = surveyAnalyticsData.responses.totalResponses;
    const startCount = surveyAnalyticsData.starts?.totalStarts || 0;
    const viewCount = surveyAnalyticsData.views.totalViews;
    if (startCount > 0 && startCount >= responseCount) {
      return `${((responseCount / startCount) * 100).toFixed(0)}`;
    }
    if (!viewCount || viewCount < responseCount) return null;
    return `${((responseCount / viewCount) * 100).toFixed(0)}`;
  };

  const getDropOffColor = (rate) => {
    const numRate = parseFloat(rate);
    if (numRate < 10) return "text-green-600";
    if (numRate < 25) return "text-yellow-600";
    return "text-red-600";
  };

  const dropOffData = useMemo(() => {
    if (!survey?.questions || !allResponses || !surveyAnalyticsData) return [];

    const totalStarted = allResponses.length;
    if (totalStarted === 0) return [];

    // Furthest question each respondent reached: prefer the tracked
    // lastQuestionIndex (recorded by the viewer), falling back to the last
    // answered question for responses saved before tracking existed.
    const furthestIndexes = allResponses.map((response) => {
      if (typeof response.lastQuestionIndex === "number") {
        return response.lastQuestionIndex;
      }
      let furthest = -1;
      survey.questions.forEach((q, idx) => {
        if (response.answers && response.answers[q.id] !== undefined) furthest = idx;
      });
      return furthest;
    });

    return survey.questions
      .map((question, originalIndex) => ({ question, originalIndex }))
      .filter(({ question }) => question.type !== "statement")
      .map(({ question, originalIndex }, displayIndex) => {
        let reachedCount = 0;
        let abandonedCount = 0;
        allResponses.forEach((response, i) => {
          if (furthestIndexes[i] >= originalIndex) reachedCount += 1;
          // Abandoned here: partial respondents whose furthest question is this one
          if (response.status === "partial" && furthestIndexes[i] === originalIndex) {
            abandonedCount += 1;
          }
        });

        const answeredCount = allResponses.filter(
          (response) => response.answers && response.answers[question.id] !== undefined
        ).length;

        const dropOffRate = reachedCount > 0 ? (abandonedCount / reachedCount) * 100 : 0;

        return {
          question,
          answered: answeredCount,
          reached: reachedCount,
          total: totalStarted,
          dropOffRate: dropOffRate.toFixed(1),
          answerRate: reachedCount > 0 ? ((answeredCount / reachedCount) * 100).toFixed(1) : "0.0",
          index: displayIndex + 1,
        };
      });
  }, [survey?.questions, allResponses, surveyAnalyticsData]);

  const trendData = useMemo(() => {
    if (!surveyAnalyticsData) return [];

    const weeklyViews = surveyAnalyticsData.views?.weeklyViews || [];
    const weeklyResponses = surveyAnalyticsData.responses?.weeklyResponses || [];

    const weekMap = {};

    weeklyViews.forEach((wv) => {
      const label = wv.weekStart
        ? new Date(wv.weekStart).toLocaleDateString("en-US", { month: "short", day: "numeric" })
        : `W${wv.weekNumber}`;
      weekMap[wv.weekNumber] = {
        ...weekMap[wv.weekNumber],
        name: label,
        views: wv.viewsInWeek,
      };
    });

    weeklyResponses.forEach((wr) => {
      const label = wr.weekStart
        ? new Date(wr.weekStart).toLocaleDateString("en-US", { month: "short", day: "numeric" })
        : `W${wr.weekNumber}`;
      weekMap[wr.weekNumber] = {
        ...weekMap[wr.weekNumber],
        name: label,
        responses: wr.responsesInWeek,
        averageTime: wr.averageTimeInWeek,
      };
    });

    return Object.keys(weekMap)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => ({
        name: weekMap[key].name || `W${key}`,
        Views: weekMap[key].views || 0,
        Responses: weekMap[key].responses || 0,
        averageTime: weekMap[key].averageTime || 0,
      }));
  }, [surveyAnalyticsData]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <div className="text-red-600 text-sm">Error: {error}</div>
        <button
          onClick={fetchAllData}
          className="text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (loading || !surveyAnalyticsData || !survey) {
    return <OverviewSkeleton />;
  }

  const completionRate = getCompletionRate();

  const funnelViews = surveyAnalyticsData.views?.totalViews || 0;
  const funnelStarts = surveyAnalyticsData.starts?.totalStarts || 0;
  const funnelCompleted = surveyAnalyticsData.responseStats?.completed || 0;
  const funnelSteps = [
    { label: "Viewed", value: funnelViews, color: "bg-indigo-500" },
    { label: "Started", value: funnelStarts, color: "bg-sky-500" },
    { label: "Completed", value: funnelCompleted, color: "bg-green-500" },
  ];
  const funnelMax = Math.max(...funnelSteps.map((s) => s.value));
  const showFunnel = funnelViews > 0 && funnelStarts > 0;

  const deviceStats = surveyAnalyticsData.deviceStats;
  const deviceTotals = deviceStats
    ? {
        mobile: (deviceStats.mobile?.completed || 0) + (deviceStats.mobile?.partial || 0),
        desktop: (deviceStats.desktop?.completed || 0) + (deviceStats.desktop?.partial || 0),
      }
    : { mobile: 0, desktop: 0 };
  const showDeviceStats = deviceTotals.mobile + deviceTotals.desktop > 0;

  return (
    <div
      className="bg-white -mt-[calc(var(--navbar-height,64px)+3.5rem)] relative overflow-hidden"
      style={{ minHeight: "calc(100vh - var(--navbar-height, 64px))" }}
    >
      <div className="absolute -top-28 left-1/4 w-72 h-72 rounded-full bg-[#EFFBFF] blur-3xl opacity-70 pointer-events-none" />
      <div className="absolute top-32 right-10 w-64 h-64 rounded-full bg-[#9AF2FF]/25 blur-3xl pointer-events-none" />

      <div className="relative px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto mb-6">
          <p className="text-sm font-medium text-gray-400">Results overview</p>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mt-2">
            <div>
              <h2 className="text-2xl font-semibold tracking-[-0.02em] text-gray-950">Survey summary stats</h2>
              <p className="text-sm text-gray-500 mt-2 max-w-xl">
                A quick health check of views, responses, completion, and where respondents drop off.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {lastUpdated && (
                <span className="text-xs text-gray-400">
                  Updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              <button
                onClick={fetchAllData}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-950 transition-all rounded-lg border border-gray-200 bg-white/80 hover:bg-white px-2.5 py-1.5 shadow-sm"
                title="Refresh data"
              >
                <RefreshCw size={14} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {surveyAnalyticsData.responseStats?.apiSubmitted > 0 && (
          <div className="flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3 mx-auto mb-5 w-full max-w-4xl">
            <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-900">
              {surveyAnalyticsData.responseStats.apiSubmitted} of {surveyAnalyticsData.responseStats.total} responses were submitted via API integration and never loaded the survey page — Views and Completion below only reflect visits to the survey link.
            </p>
          </div>
        )}

        {/* Overview Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mx-auto mb-6 w-full max-w-4xl">
          {[
            {
              title: "Views",
              value: surveyAnalyticsData.views.totalViews,
              change: `${surveyAnalyticsData.percentageChangeFromLastWeek?.views || 0}%`,
            },
            {
              title: "Responses",
              value: surveyAnalyticsData.responses.totalResponses,
              change: `${surveyAnalyticsData.percentageChangeFromLastWeek?.responses || 0}%`,
            },
            {
              title: "Avg. Time",
              value: formatTime(surveyAnalyticsData.responses.totalAverageTime),
              change: `${formatTime(surveyAnalyticsData.percentageChangeFromLastWeek?.averageTime || 0)}`,
            },
            {
              title: "Completion",
              value: completionRate != null ? `${completionRate}%` : "—",
              change: `${(surveyAnalyticsData?.percentageChangeFromLastWeek?.completionRate || 0).toFixed(1)}%`,
              hideChange: completionRate == null,
            },
          ].map((card, index) => (
            <div key={index} className="rounded-2xl p-5 bg-white/90 backdrop-blur-sm border border-line/70 shadow-[0_12px_32px_rgba(15,23,42,0.04)] hover:shadow-[0_16px_40px_rgba(15,23,42,0.07)] transition-all duration-200">
              <p className="text-xs text-gray-500 font-medium mb-1">{card.title}</p>
              <p className="text-2xl font-semibold text-gray-900">{card.value}</p>
              {card.hideChange ? (
                <div className="mt-1 h-4" />
              ) : (
                <div className="mt-1 flex items-center gap-1">
                  <span
                    className={`text-xs font-medium ${
                      card.change.includes("-") ? "text-red-500" : "text-green-500"
                    }`}
                  >
                    {card.change.includes("-") ? "" : "+"}
                    {card.change}
                  </span>
                  <span className="text-xs text-gray-400">vs last week</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Conversion Funnel: view -> start -> complete */}
        {showFunnel && (
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 mx-auto mb-5 w-full max-w-4xl border border-line/70 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
            <div className="mb-4 flex items-start justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Conversion Funnel</h3>
              <div
                className="flex items-center gap-1 text-xs text-gray-400"
                title="Viewed = loaded the survey page. Started = moved past the welcome screen to the first question. Completed = submitted the survey."
              >
                <Info size={14} />
              </div>
            </div>
            <div className="space-y-3">
              {funnelSteps.map((step, index) => {
                const prevValue = index > 0 ? funnelSteps[index - 1].value : null;
                const stepRate =
                  prevValue > 0 ? Math.min(100, (step.value / prevValue) * 100) : null;
                const barWidth = funnelMax > 0 ? Math.max(2, (step.value / funnelMax) * 100) : 0;
                return (
                  <div key={step.label} className="flex items-center gap-3">
                    <span className="w-20 text-xs text-gray-500 font-medium flex-shrink-0">
                      {step.label}
                    </span>
                    <div className="flex-1 h-7 bg-gray-50 rounded-lg overflow-hidden">
                      <div
                        className={`h-full ${step.color} rounded-lg transition-all duration-500 flex items-center`}
                        style={{ width: `${barWidth}%` }}
                      >
                        <span className="text-xs font-semibold text-white px-2 whitespace-nowrap">
                          {step.value.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <span className="w-14 text-right text-xs text-gray-500 flex-shrink-0">
                      {stepRate != null ? `${stepRate.toFixed(0)}%` : ""}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Percentages show conversion from the previous step.
            </p>
          </div>
        )}

        {/* Response Breakdown */}
        {surveyAnalyticsData?.responseStats && (
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 mx-auto mb-5 w-full max-w-4xl border border-line/70 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Response Breakdown</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-4 bg-gray-50/80 border border-gray-100 rounded-2xl">
                <div className="text-2xl font-semibold text-green-600">
                  {surveyAnalyticsData.responseStats.completed}
                </div>
                <div className="text-xs text-gray-500 mt-1">Completed</div>
                <div className="text-xs text-green-500 font-medium">
                  {surveyAnalyticsData.responseStats.total > 0
                    ? Math.round(
                        (surveyAnalyticsData.responseStats.completed /
                          surveyAnalyticsData.responseStats.total) *
                          100
                      )
                    : 0}
                  %
                </div>
              </div>
              <div className="text-center p-4 bg-gray-50/80 border border-gray-100 rounded-2xl">
                <div className="text-2xl font-semibold text-amber-500">
                  {surveyAnalyticsData.responseStats.partial}
                </div>
                <div className="text-xs text-gray-500 mt-1">Partial</div>
                <div className="text-xs text-amber-500 font-medium">
                  {surveyAnalyticsData.responseStats.total > 0
                    ? Math.round(
                        (surveyAnalyticsData.responseStats.partial /
                          surveyAnalyticsData.responseStats.total) *
                          100
                      )
                    : 0}
                  %
                </div>
              </div>
            </div>

            {/* Device breakdown (mobile vs desktop) */}
            {showDeviceStats && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                {["mobile", "desktop"].map((device) => {
                  const completed = deviceStats[device]?.completed || 0;
                  const total = deviceTotals[device];
                  const deviceCompletionRate =
                    total > 0 ? Math.round((completed / total) * 100) : null;
                  return (
                    <div
                      key={device}
                      className="text-center p-4 bg-gray-50/80 border border-gray-100 rounded-2xl"
                    >
                      <div className="text-2xl font-semibold text-gray-900">{total}</div>
                      <div className="text-xs text-gray-500 mt-1 capitalize">{device}</div>
                      <div className="text-xs text-gray-400 font-medium">
                        {deviceCompletionRate != null
                          ? `${deviceCompletionRate}% completed`
                          : "—"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Trend Chart */}
        {trendData.length > 1 && (
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 mx-auto mb-5 w-full max-w-4xl border border-line/70 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Weekly Trends</h3>
            <div className="bg-gray-50/80 border border-gray-100 rounded-2xl p-4" style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradResponses" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                  <Tooltip content={<TrendTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="Views"
                    stroke="#6366f1"
                    strokeWidth={2}
                    fill="url(#gradViews)"
                    dot={{ r: 3, fill: "#6366f1" }}
                    activeDot={{ r: 5 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="Responses"
                    stroke="#22c55e"
                    strokeWidth={2}
                    fill="url(#gradResponses)"
                    dot={{ r: 3, fill: "#22c55e" }}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-6 mt-3">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-indigo-500" />
                <span className="text-xs text-gray-500">Views</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-xs text-gray-500">Responses</span>
              </div>
            </div>
          </div>
        )}

        {/* Drop-off Funnel */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 mx-auto w-full max-w-4xl border border-line/70 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Question Drop-off</h3>
              <p className="text-xs text-gray-500 mt-1">
                Based on {allResponses?.length || 0} total responses (completed + partial)
              </p>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-400" title="Drop-off = respondents who reached a question but abandoned the survey there, out of everyone who reached it. Skipped optional questions and logic jumps don't count as drop-off.">
              <Info size={14} />
            </div>
          </div>

          {dropOffData.length > 0 ? (
            <div className="overflow-x-auto bg-white rounded-2xl border border-gray-100">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Question
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase w-28">
                      Answered / Reached
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase w-24">
                      Drop-off
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dropOffData.map((item, index) => (
                    <tr
                      key={index}
                      className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {QUESTION_TYPE_ICONS[item.question.type] && (
                            <div
                              className={`flex items-center justify-center gap-1 px-2 py-1 rounded ${
                                QUESTION_TYPE_ICONS[item.question.type].bgColor
                              }`}
                            >
                              {createElement(QUESTION_TYPE_ICONS[item.question.type].icon, {
                                className: "w-3 h-3 text-gray-600",
                              })}
                              <span className="text-xs text-gray-600 font-medium">{item.index}</span>
                            </div>
                          )}
                          <span className="text-sm text-gray-700 truncate max-w-[200px] sm:max-w-md">
                            {item.question.text}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm text-gray-600">
                          {item.answered}/{item.reached}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-medium ${getDropOffColor(item.dropOffRate)}`}>
                          {item.dropOffRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-sm text-gray-400">
              No response data available yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyAnalyticsOverview;
